import React from "react"
import { useShallow } from "zustand/react/shallow"
import scrollToElement from "utilities/scrollToElement"
import { formSchemas, SCROLLABLE_AUTHORING_CONTAINER, previewFormSchemas } from "constants/authoring"

// hooks
import useAuthoringViewStore from "../stores/useAuthoringViewStore"
import useAuthoringDataStore from "../stores/useAuthoringDataStore"
import useAuthoringStore from "../stores/useAuthoringStore"
import { useGetAuthoringComments } from "api/hooks/studies/useAuthoringApi"
import { useDeleteAuthoringComment } from "api/hooks/studies/useAuthoringApi"

const highlightSelectedField = (id) => {
  if (id) {
    let field = document.getElementById(id)
    if (field) {
      field.classList.add("highlighted-field")
    }
  }
}

const useAuthoringActions = () => {
  const { formData, forkedStudyData, setForkedStudyData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
      forkedStudyData: state.forkedStudyData,
      setForkedStudyData: state.setForkedStudyData,
    }))
  )

  const { authoringId, setShowOverrideModal, setOverrideCallback } = useAuthoringStore(
    useShallow((state) => ({
      authoringId: state.authoringId,
      setShowOverrideModal: state.setShowOverrideModal,
      setOverrideCallback: state.setOverrideCallback,
    }))
  )

  const {
    isReviewerView,
    formSection,
    showPagePreview,
    formView,
    setFormView,
    setFormSection,
    setShowDeleteForkModal,
    forkPathToDelete,
    setForkPathToDelete,
    forkRegToDelete,
    setForkRegToDelete,
  } = useAuthoringViewStore(
    useShallow((state) => ({
      isReviewerView: state.isReviewerView,
      formSection: state.formSection,
      showPagePreview: state.showPagePreview,
      formView: state.formView,
      setFormView: state.setFormView,
      setFormSection: state.setFormSection,
      setShowDeleteForkModal: state.setShowDeleteForkModal,
      forkPathToDelete: state.forkPathToDelete,
      setForkPathToDelete: state.setForkPathToDelete,
      forkRegToDelete: state.forkRegToDelete,
      setForkRegToDelete: state.setForkRegToDelete,
    }))
  )

  const [sectDropdownOptions, setSectDropdownOptions] = React.useState([])

  const deleteAuthoringComment = useDeleteAuthoringComment(authoringId)
  const { data: comments = [] } = useGetAuthoringComments(authoringId)

  // set sections dropdown menu items
  React.useEffect(() => {
    if (formSchemas) {
      let dropdownOptions = []
      Object.keys(formSchemas).forEach((skey) => {
        let section = { id: skey, text: formSchemas[skey].title }
        dropdownOptions.push(section)
      })
      setSectDropdownOptions(dropdownOptions)
    }
  }, [formData?.protocol?.study_type])

  const scrollToField = (id, scrollType = "instant") => {
    const element = document.getElementById(id)
    const container = isReviewerView
      ? document.getElementById(SCROLLABLE_AUTHORING_CONTAINER.REVIEW_ID)
      : document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.FORM_CLASSNAME)[0]

    highlightSelectedField(id + "-field")

    scrollToElement({
      scrollableElement: container,
      targetElement: element,
      offsetTop: 100,
      scrollType: "instant",
    })
  }

  const handleOverride = (cb) => {
    setShowOverrideModal(true)
    setOverrideCallback(cb)
  }

  const handleSection = (val) => {
    if (isReviewerView === false) {
      const container = document.getElementsByClassName(SCROLLABLE_AUTHORING_CONTAINER.FORM_CLASSNAME)[0]
      if (container) container.scrollTop = 0
    }

    if (showPagePreview === true && previewFormSchemas?.[val?.id]?.properties?.[formView.id]) {
      let schema = { ...previewFormSchemas[val.id].properties[formView.id] }
      if (!schema || Object.keys(schema).length === 0) {
        let key = Object.keys(previewFormSchemas[formSection.id].properties)[0]
        let sectionSchema = previewFormSchemas[formSection.id].properties[key]
        let formView = { id: sectionSchema.title.toLowerCase(), text: sectionSchema.title }
        setFormView(formView)
      }
    }
    setFormSection(val)
  }

  const onChangeSection = (val) => {
    let found = sectDropdownOptions.find((item) => item.text === val)
    if (found) {
      handleSection(found)
      const container = document.getElementsByClassName("right-section")[0]
      if (container) {
        container.scrollTo({
          top: 0,
          behavior: "instant",
        })
      }
    }
  }

  const onDeleteFork = () => {
    let reg = forkRegToDelete === "eudra" ? "eudract" : forkRegToDelete

    const fsd = forkedStudyData || {}
    if (fsd?.[reg].hasOwnProperty(forkPathToDelete)) {
      delete fsd[reg][forkPathToDelete]
      setForkedStudyData(fsd)
      setForkPathToDelete(null)
      setForkRegToDelete(null)
      setShowDeleteForkModal(false)

      const forkedJpath = `root_${forkPathToDelete.split(".").join("_")}_${reg}`
      const commentsToDelete = comments.filter((comment) => comment.field_key === forkedJpath)

      commentsToDelete.forEach((comment) => {
        deleteAuthoringComment({ id: comment.id + "/" })
      })
    }
  }

  const registryOptions = React.useMemo(() => {
    const map = {
      global: "Global",
      ctg: "CTG",
      eudra: "EudraCT",
      eupas: "EMA RWD",
      ctis: "CTIS",
      jrct: "jRCT",
    }

    if (formSection?.id && formSchemas) {
      const registries = []
      const sectionSchemas = formSchemas[formSection.id].properties
      for (const regKey in sectionSchemas) {
        if (showPagePreview && regKey !== "global") registries.push(regKey)
        else if (!showPagePreview) registries.push(regKey)
      }

      return registries.map((reg) => ({ value: reg, label: map[reg] }))
    }

    return []
  }, [formSection])

  return {
    scrollToField,
    handleOverride,
    onChangeSection,
    sectDropdownOptions,
    onDeleteFork,
    registryOptions,
  }
}

export default useAuthoringActions
